// src/utilities/formattedDisplayComponents/FormRulesValidator.ts
import Ajv, { ErrorObject } from "ajv";
import { ValidationResult } from "./FormConfigValidator";

const addError = (errors: string[], path: string, message: string) => {
  errors.push(`Error at ${path}: ${message}`);
};

// Validator for a 'check' object. This is the most complex piece.
const validateCheck = (check: any, path: string, errors: string[]) => {
  if (typeof check !== 'object' || check === null) {
    addError(errors, path, 'must be an object.');
    return;
  }

  // Check for the Time Difference shape
  if (check.type === 'TIME_DIFF') {
    if (typeof check.startField !== 'string') addError(errors, `${path}.startField`, 'is required for TIME_DIFF.');
    if (typeof check.endField !== 'string') addError(errors, `${path}.endField`, 'is required for TIME_DIFF.');
    if (typeof check.minMinutes !== 'number') addError(errors, `${path}.minMinutes`, 'is required and must be a number for TIME_DIFF.');
    return;
  }

  // Otherwise, assume it's a standard Field Check
  if (typeof check.field !== 'string') addError(errors, `${path}.field`, 'is missing or not a string.');
  if (typeof check.operator !== 'string') addError(errors, `${path}.operator`, 'is missing or not a string.');
  if (typeof check.value === 'undefined') addError(errors, `${path}.value`, 'is a required field.');
};

const validateRule = (rule: any, path: string, errors: string[]) => {
  if (typeof rule !== 'object' || rule === null) {
    addError(errors, path, 'must be an object.');
    return;
  }
  // Check required fields
  if (typeof rule.id !== 'string') addError(errors, `${path}.id`, 'is missing or not a string.');
  if (typeof rule.description !== 'string') addError(errors, `${path}.description`, 'is missing or not a string.');
  if (typeof rule.errorMessage !== 'string') addError(errors, `${path}.errorMessage`, 'is missing or not a string.');
  if (typeof rule.errorField !== 'string') addError(errors, `${path}.errorField`, 'is missing or not a string.');

  // Check `checks` array
  if (!Array.isArray(rule.checks) || rule.checks.length === 0) {
    addError(errors, `${path}.checks`, 'must be a non-empty array.');
  } else {
    rule.checks.forEach((chk: any, i: number) => validateCheck(chk, `${path}.checks[${i}]`, errors));
  }
  
  // Check optional `when` array
  if (rule.when && !Array.isArray(rule.when)) {
    addError(errors, `${path}.when`, 'must be an array if it exists.');
  }
};

/**
 * Main validation function for the entire Form Rules object.
 * @param rules The parsed JSON object for the form rules (unwrapped).
 * @returns A ValidationResult object.
 */
export const validateFormRules = (rules: any): ValidationResult => {
  const errors: string[] = [];

  if (typeof rules !== 'object' || rules === null) {
    return { isValid: false, errors: ['The root rules object must be an object.'] };
  }

  // The 'rules' object is the unwrapped form rules.
  if (typeof rules.formId !== 'string' || !rules.formId) {
    addError(errors, 'formId', 'is missing or not a string.');
  }

  if (!Array.isArray(rules.validationRules)) {
    addError(errors, 'validationRules', 'is missing or not an array.');
  } else {
    rules.validationRules.forEach((rule: any, i: number) => {
      validateRule(rule, `validationRules[${i}]`, errors);
    });
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};